import { Alert, Button, Skeleton, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useUser } from '../../../context/page-context';
import { shortDateTimeFormatter } from '../../../utils/format-date';
import { useSyncStatus } from '../hooks/use-sync-log-status';
import type { SyncLogIdentifier, SyncLogStatus } from '../types';
import { SyncLogErrorTable } from './sync-log-error-table';

export function SyncLogStatusAlert({
  syncFor,
  allowViewErrors,
}: {
  syncFor: SyncLogIdentifier;
  allowViewErrors: boolean;
}) {
  const { data: status, isLoading, error, refetch } = useSyncStatus(syncFor);
  const { isSparkStaff } = useUser();

  if (isLoading) {
    return <Skeleton />;
  }

  if (error || !status) {
    return (
      <Alert
        variant="error"
        title={t('features:synclog.components.sync_log_status_alert.load_error.title')}
        headingLevel="h2"
      >
        <div className="flex w-full flex-col items-start gap-2">
          <Text>
            {t('features:synclog.components.sync_log_status_alert.load_error.description')}
          </Text>
          <Button onClick={() => refetch()} variant="secondary">
            {t('buttons:reload')}
          </Button>
        </div>
      </Alert>
    );
  }

  if (!status.last_full_sync) {
    const dataType = t(`features:synclog.common.dataType.${syncFor.dataType}`);
    return (
      <Alert variant="warning" headingLevel="h2">
        <div className="flex w-full flex-col items-start gap-2">
          <Text size="md" variant="primary">
            {t(`features:synclog.components.sync_log_status_in_progress.title`, {
              data_type: dataType.charAt(0).toUpperCase() + dataType.slice(1),
            })}
          </Text>
          <Button onClick={() => refetch()} variant="secondary">
            {t('buttons:reload')}
          </Button>
        </div>
      </Alert>
    );
  }
  return (
    <SyncLogStatusComponent
      syncFor={syncFor}
      status={status}
      allowViewErrors={allowViewErrors}
      showApiErrors={isSparkStaff}
    />
  );
}

function SyncLogStatusComponent({
  syncFor,
  status,
  allowViewErrors,
  showApiErrors,
}: {
  syncFor: SyncLogIdentifier;
  status: SyncLogStatus;
  allowViewErrors: boolean;
  showApiErrors?: boolean;
}) {
  const [showSyncErrors, setShowSyncErrors] = useState(false);
  const toggleShowSyncErrors = () =>
    setShowSyncErrors((prev) => (!allowViewErrors ? false : !prev));

  const isSyncLogErrors = status.error_count > 0;
  const dataType = t(`features:synclog.common.dataType.${syncFor.dataType}`);

  const allowToggle = isSyncLogErrors && allowViewErrors;

  const syncFatalError = status.last_full_sync_succeeded === false;

  return (
    <Alert
      variant={
        syncFatalError ? 'error' : isSyncLogErrors || !status.last_full_sync ? 'warning' : 'success'
      }
      headingLevel="h2"
    >
      <div className="flex w-full flex-col gap-2">
        <Text size="md" variant="primary">
          {t(
            !syncFatalError
              ? `features:synclog.components.sync_log_status_alert.title`
              : `features:synclog.components.sync_log_status_failed.title`,
            {
              data_type: dataType.charAt(0).toUpperCase() + dataType.slice(1),
              error_count: status.error_count,
            },
          )}
        </Text>
        <Text size="md" variant="secondary">
          {t(
            !syncFatalError
              ? `features:synclog.components.sync_log_status_alert.description`
              : `features:synclog.components.sync_log_status_failed.description`,
            {
              data_type: dataType,
              error_count: status.error_count,
            },
          )}
        </Text>
        <Text size="sm" variant="secondary">
          {t(`features:synclog.components.sync_log_status_alert.last_sync.combined`, {
            full_date_time: shortDateTimeFormatter
              .format(new Date(status.last_full_sync))
              .replace(',', ' at'),
            partial_date_time: shortDateTimeFormatter
              .format(new Date(status.last_partial_sync))
              .replace(',', ' at'),
          })}
        </Text>
        {allowToggle && (
          <>
            <Button variant="secondary" onClick={toggleShowSyncErrors}>
              {`${showSyncErrors ? t('buttons:hide') : t('buttons:show')} ${t(`features:synclog.components.sync_log_status_alert.errors`)}`}
            </Button>
            {showSyncErrors && (
              <div className="w-full">
                <SyncLogErrorTable syncFor={syncFor} showApiErrors={!!showApiErrors} />
              </div>
            )}
          </>
        )}
      </div>
    </Alert>
  );
}
